import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { compact, some } from 'lodash';

import { DropdownItem } from '@app/components';
import { useAvailabilityContext, useRecordSummaryContext } from '@app/contexts';
import { useFeatureFlag } from '@app/hooks';
import { Feature } from '@app/constants';

export enum CopyType {
  firstAvailable,
  specificVolume,
}

export const usePlaceHoldCopies = ({ isOpen }: { isOpen: boolean }) => {
  const { $t } = useIntl();
  const [copies, setCopies] = useState<DropdownItem[]>([]);
  const [selectedCopy, setSelectedCopy] = useState<DropdownItem | null>(null);
  const { instance } = useRecordSummaryContext();
  const { availability } = useAvailabilityContext();
  const { isActive: isFirstAvailableCopyFlagActive } = useFeatureFlag({
    name: Feature.firstAvailableCopyPlaceHold,
  });

  const shouldDisplayCopiesDropdown = copies.length > 1;
  const initialCopyType =
    !isFirstAvailableCopyFlagActive && shouldDisplayCopiesDropdown
      ? CopyType.specificVolume
      : CopyType.firstAvailable;

  const [copyType, setCopyType] = useState<CopyType>(initialCopyType);

  useEffect(() => {
    const recordAvailability = availability[instance.id];

    if (
      recordAvailability &&
      some(recordAvailability.availabilityData, 'volume')
    ) {
      const recordCopies = recordAvailability.availabilityData.map(
        holdingAvailability => {
          const {
            library,
            volume = $t({
              id: 'placeHoldModal.noVolumeInformationProvided',
              defaultMessage: 'No volume information provided',
            }),
            location,
          } = holdingAvailability;

          const locationLabel = library?.name
            ? `${location.name} (${library?.name})`
            : location.name;
          const labelFields = compact([
            // Removes outer parentheses from volume
            volume.replace(/^\((.*)\)$/, '$1'),
            locationLabel,
          ]);

          return { label: labelFields.join(' • '), ...holdingAvailability };
        }
      ) as DropdownItem[];

      setCopies(recordCopies);
    }
  }, [instance.id, availability, $t]);

  useEffect(() => {
    if (isOpen) {
      setCopyType(initialCopyType);

      if (initialCopyType === CopyType.specificVolume) {
        setSelectedCopy(copies[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copies, isOpen]);

  return {
    copies,
    copyType,
    initialCopyType,
    selectedCopy,
    shouldDisplayCopiesDropdown,
    setSelectedCopy,
    setCopyType,
  };
};
